/**
 * @file    data.js
 * @author  Daniel Naftzger
 * @created 2026-02-26
 * @updated 2026-03-05
 *
 * @description
 * Portfolio data for the site owner. Centralizes all personal info, skills,
 * projects, and work experience so content updates don't require touching
 * component files.
 *
 * NOTE: Contact info (email, GitHub, LinkedIn) is intentionally NOT stored
 * here. It lives only on the server and is returned after reCAPTCHA
 * verification. See verify-recaptcha.php and ContactSection.js.
 */

/**
 * @typedef {Object} Skill
 * @property {string} category -
 * @property {string[]} items -
 */

/**
 * @typedef {Object} ArchitectureLayer
 * @property {string}   name   - Layer name (e.g. "Frontend", "API", "Database")
 * @property {string[]} tech   - Technologies used in this layer
 * @property {string}   detail - Description of what this layer does
 */

/**
 * @typedef {Object} Architecture
 * @property {string}              summary - One-line architectural summary
 * @property {ArchitectureLayer[]} layers  - System layers from top to bottom
 * @property {string[]}            flow    - Ordered data-flow steps
 */

/**
 * @typedef {Object} Project
 * @property {string}        name         -
 * @property {string}        year         -
 * @property {string}        desc         -
 * @property {string[]}      tags         -
 * @property {string}        link         -
 * @property {Architecture}  [architecture] - Optional architectural overview
 */

/**
 * @typedef {Object} Experience
 * @property {string} role -
 * @property {string} company -
 * @property {string} period -
 * @property {string[]} bullets -
 */

/**
 * @typedef {Object} PortfolioData
 * @property {string} name -
 * @property {string} title -
 * @property {string} tagline -
 * @property {string} about -
 * @property {Skill[]} skills -
 * @property {Project[]} projects -
 * @property {Experience[]} experience -
 */

/** @type {PortfolioData} */
const data = {
  name: "Daniel Naftzger",
  title: "Software Developer",
  tagline: "Building elegant systems at scale.",
  about: "I am a software developer with 3+ years of experience in web development and software tool design",
  skills: [
    { category: "Languages", items: ["Javascript", "Typescript", "Python", "SQL", "HTML", "CSS"] },
    { category: "Frontend", items: ["React", "React Native", "Expo", "Angular", "Next.js", "Canvas.js"] },
    { category: "Backend", items: ["Node.js", "ASP.NET Core", "PostgreSQL"] },
  ],
  projects: [
    {
      name: "Senior Project - Data Visualization Web Application",
      year: "2021",
      desc: "A web application that takes financial data from the WCU School of Business and displays it through various graphs, charts, and data visualizations.",
      tags: ["Node.js", "PostgreSQL", "React", "express.js"],
      link: "#",
      architecture: {
        summary:
          "Three-tier web application with a React SPA consuming a RESTful API backed by PostgreSQL.",
        layers: [
          {
            name: "Frontend",
            tech: ["React", "Chart.js", "Canvas.js"],
            detail:
              "Single-page application rendering interactive charts and data tables. Components fetch data from the API layer and transform it into visual representations.",
          },
          {
            name: "API",
            tech: ["Node.js", "Express.js"],
            detail:
              "RESTful endpoints handle authentication, query building, and data aggregation. Middleware validates requests and caches frequent queries.",
          },
          {
            name: "Database",
            tech: ["PostgreSQL"],
            detail:
              "Normalized schema storing financial records imported from the School of Business. Views and materialized views pre-aggregate common report shapes.",
          },
        ],
        flow: [
          "User selects a date range and metric in the React UI",
          "Frontend sends a parameterized GET request to the Express API",
          "API builds and executes a SQL query against PostgreSQL",
          "Results are aggregated and returned as JSON",
          "React renders the data using Chart.js / Canvas.js",
        ],
      },
    },
    {
      name: "IceArts Animation Portfolio",
      year: "2026",
      desc: "A portfolio website for a 2D character and FX animator, featuring video reels, image galleries with a lightbox viewer, and a headless CMS for content management.",
      tags: ["React", "TypeScript", "Sanity CMS", "Vercel"],
      link: "https://ice-artz-portfolio.vercel.app/",
      architecture: {
        summary:
          "Jamstack SPA built with React and TypeScript, powered by Sanity as a headless CMS, with Vercel serverless functions handling reCAPTCHA verification and email delivery.",
        layers: [
          {
            name: "Frontend",
            tech: ["React 19", "TypeScript", "React Router", "CSS Modules"],
            detail:
              "Single-page application with client-side routing. Pages fetch content from Sanity via GROQ queries at runtime. Features include video reel embeds, image galleries with a fullscreen lightbox, password-gated sections, and a reCAPTCHA-protected contact form. Styled with scoped CSS Modules and global CSS custom properties for theming.",
          },
          {
            name: "Content Management",
            tech: ["Sanity CMS", "GROQ", "Sanity Studio"],
            detail:
              "Headless CMS storing all dynamic content — portfolio projects, bio text, resume files, video reels, and site settings like password protection. A separate Sanity Studio app provides a visual editing interface for the client. Images are served through Sanity's CDN with on-the-fly resizing and format conversion.",
          },
          {
            name: "Serverless API",
            tech: ["Vercel Functions", "Google reCAPTCHA v3", "Resend"],
            detail:
              "Two serverless endpoints on Vercel: one verifies invisible reCAPTCHA v3 tokens and scores requests as human or bot, the other forwards validated contact form submissions as emails via the Resend API. Secret keys stay server-side and are never exposed to the browser.",
          },
          {
            name: "Build & Hosting",
            tech: ["Vite", "Vercel", "GitHub"],
            detail:
              "Vite compiles TypeScript and bundles the app into optimized static files. Pushes to main trigger automatic Vercel builds and deployments to a global edge CDN. Feature branches generate preview deployments for testing before merge.",
          },
        ],
        flow: [
          "Client manages content (projects, reels, bio, resume) through Sanity Studio",
          "User visits the site and the React SPA loads from Vercel's edge CDN",
          "React Router matches the URL and renders the appropriate page component",
          "Page components fetch content from Sanity's API via GROQ queries",
          "Sanity returns JSON data and serves optimized images from its CDN",
          "User submits the contact form, triggering an invisible reCAPTCHA v3 check",
          "Vercel serverless function verifies the token score with Google",
          "If human, the function forwards the message via Resend's email API",
        ],
      },
    },
    {
      name: "Personal Website",
      year: "2026",
      desc: "This website",
      tags: ["Javascript", "Node.js", "Next.js", "React"],
      link: "#",
      architecture: {
        summary:
          "Single-page React app with a PHP reCAPTCHA backend, deployed to cPanel shared hosting via a GitHub Actions CI/CD pipeline.",
        layers: [
          {
            name: "Frontend",
            tech: ["React 19", "Create React App"],
            detail:
              "Single-page application with component-based architecture. Styles are JS objects for dynamic hover and responsive behavior. Mobile layout uses className hooks targeted by @media rules. Content is data-driven via a centralized data.js file.",
          },
          {
            name: "Backend",
            tech: ["PHP", "Google reCAPTCHA v2"],
            detail:
              "A single PHP endpoint verifies reCAPTCHA tokens against Google's API and returns contact info only after successful verification. Contact details never appear in the frontend bundle. CORS and environment config are loaded from a server-side .env file.",
          },
          {
            name: "CI/CD",
            tech: ["GitHub Actions", "FTP Deploy"],
            detail:
              "Pushes to main or staging trigger an automated pipeline that installs dependencies, builds the React app, copies the PHP backend into the build output, and deploys via FTP to the appropriate cPanel directory.",
          },
          {
            name: "Hosting",
            tech: ["cPanel", "Shared Hosting"],
            detail:
              "Static React build and PHP endpoint are served from the same cPanel account. Production deploys to public_html/ and staging to public_html/staging/. The production .env is managed manually in cPanel and excluded from all deployments.",
          },
        ],
        flow: [
          "Developer pushes to main or staging branch",
          "GitHub Actions builds the React app and bundles the PHP backend",
          "Build output is deployed via FTP to the cPanel hosting directory",
          "User visits the site and the React SPA loads in the browser",
          "User completes the reCAPTCHA challenge and clicks Show Contact Info",
          "Frontend POSTs the captcha token to verify-recaptcha.php",
          "PHP verifies the token with Google and returns contact info if valid",
          "React displays the email, GitHub, and LinkedIn links",
        ],
      },
      // Add architecture here when ready
    },
    {
      name: "Health Guidelines Mobile Application",
      year: "2026",
      desc: "Gives healthy eating suggestions to the user based on food and dietary selection",
      tags: ["React Native", "Expo", "Claude API"],
      link: "#",
      architecture: {
        summary:
          "Cross-platform mobile app using React Native + Expo, powered by the Claude API for personalized dietary guidance.",
        layers: [
          {
            name: "Mobile Client",
            tech: ["React Native", "Expo"],
            detail:
              "Cross-platform UI with native feel. Handles food selection, dietary preferences, and displays AI-generated suggestions in a conversational interface.",
          },
          {
            name: "AI Layer",
            tech: ["Claude API", "Prompt Engineering"],
            detail:
              "Structured prompts send the user's food selections and dietary constraints to Claude. Responses are parsed into actionable meal suggestions and nutritional notes.",
          },
        ],
        flow: [
          "User selects foods and dietary preferences in the app",
          "Selections are formatted into a structured prompt",
          "Prompt is sent to the Claude API",
          "AI response is parsed into meal suggestions",
          "Suggestions are displayed with nutritional context",
        ],
      },
    },
  ],
  experience: [
    {
      role: "IT Administrator",
      company: "Carolina Creations Landscapes, Inc",
      period: "2022 – 2024",
      bullets: [
        "Administration of the company CRM (Aspire, Service Titan): User administration, integration/management of third-party applications, reviewing update notes and managing support tickets sent to Aspire's support team.",
        "Created solutions for administrative automation, and data handling with Python within the Aspire platform.",
        "Managed Microsoft 365 cloud services for over 80 users using the 365 Business Standard Suite, Microsoft Entra, and Microsoft Defender.",
        "Administered MDM for a fleet of over 80 iOS devices via MaaS360.",
        "Automated administrative tasks, including new device configuration, by developing tools with Windows Powershell and Command Shell.",
      ],
    },
    {
      role: "Application Support Engineer",
      company: "Rater8",
      period: "2020 – 2021",
      bullets: [
        "Collaborated with the development team to enhance current software solutions.",
        "Engaged with CEO, CTO, and rater8 team to define software specifications for new features.",
        "Engineered software to monitor health and efficiency of rater8's internal systems, including data flow and processing.",
        "Designed software for client-facing employees to enhance client communications.",
        "Developed robust solutions with ASP.NET(C#), SQL on Microsoft Azure, Entity Framework, Angular JS, Kendo UI.",
      ],
    },
    {
      role: "Rater8 Internship - Google My Business Consultant",
      company: "Rater8",
      period: "2019 – 2020",
      bullets: [
        "Collaborated with clients to drive website traffic by improving Google My Business listing visibility.",
        "Guided clients in navigating the GMB platform, resolving listing issues.",
        "Execute multiple web-admin duties to maintain rater8 platform and produce client profiles.",
        "Facilitated rater8 software setup by offering clear client instructions.",
      ],
    },
  ],
};

export default data;